import styled, { css } from 'styled-components';
import { KeyboardEvent, ReactNode, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Input } from 'reactstrap';
import OveryieldLogoNameIcon from '../icons/OveryieldLogoNameIcon';
import { useLandAdvisorContext } from '../../contexts/LandAdvisorContext';
import NewTabAnchor from '../basics/NewTabAnchor';
import useLandAdvisorAgent from './hooks/useLandAdvisorAgent';
import SendButton from './buttons/SendButton';
import LoadingDots from './buttons/LoadingDots';
import { LandAdvisorMessage } from '../../types/landAdvisor';

// Container for the left pane content
const PaneContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Header in the top left
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
  font-weight: 400;
  font-size: 1.05rem;
  color: #fff;
  padding: 1.2rem 1.5rem 0.5rem 1.5rem;
  text-align: left;
  text-transform: none;
`;

// Content area below header
const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
`;

const darkScrollbar = (track = '#23272f', thumb = '#3a3f46') => css`
  &::-webkit-scrollbar {
    width: 10px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${track};
  }
  &::-webkit-scrollbar-thumb {
    background: ${thumb};
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #4b5158;
  }

  scrollbar-width: thin;
  scrollbar-color: ${thumb} ${track};
`;

// Scrollable area for messages
const MessagesScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1rem 1rem 200px; // space for input footer
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${darkScrollbar('#23272f', '#3a3f46')};
`;

// Footer area for input
const InputFooter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.5rem 0 1rem;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: none;
  z-index: 4;
  pointer-events: auto;
`;

// Flex container for input and send button
const InputRow = styled.div`
  display: flex;
  width: 100%;
  max-width: none;
  gap: 8px;
  align-items: center;
  padding: 0 1rem;
`;

// Centered input wrapper for a no-messages state
const CenteredInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.5rem;
`;

// Message styles
const MessageBubble = styled.div<{ $role: string }>`
  ${({ $role }) =>
    $role === 'user'
      ? css`
          background: #3a3f45;
          color: #ffffff;
          padding: 10px 14px;
          border-radius: 14px;
          max-width: 80%;
          word-break: break-word;
          font-size: 0.9rem;
          line-height: 1.5;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        `
      : css`
          background: transparent;
          color: #e6eef2;
          padding: 6px 12px;
          margin: 0 0.5rem;
          border-radius: 0;
          max-width: 100%;
          word-break: break-word;
          font-size: 0.9rem;
          line-height: 1.7;
          box-shadow: none;
        `};

  p {
    margin-bottom: ${({ $role }) => ($role === 'user' ? 0 : '0.5rem')};
  }

  // Markdown/code styling inside messages (shared)
  pre {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    color: inherit;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono',
      'Courier New', monospace;
    font-size: 0.85rem;
    line-height: 1.45;
    margin: 0;
    overflow: auto;
    padding: 8px;
    code {
      background: none;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.08);
    padding: 2px 6px;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono',
      'Courier New', monospace;
    font-size: 0.85rem;
    line-height: 1.35;
  }

  a {
    color: ${({ $role }) => ($role === 'user' ? '#ffffff' : '#dfe8ef')};
    text-decoration: underline;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  width: auto;
  max-width: none;
  min-height: 48px;
  max-height: 168px;
  padding: 0.85rem 1.2rem;
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.9rem;
  line-height: 1.5;
  background: #373b40;
  color: #fff;
  caret-color: #fff;
  outline: none;
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.06);
  ::placeholder {
    color: #c7c9cc;
    opacity: 1;
  }

  /* WebKit / Blink */
  &::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
  textarea::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
  /* Firefox */
  scrollbar-width: none !important;
  /* IE 10+ */
  -ms-overflow-style: none !important;

  // Override reactstrap input styles
  && {
    &:focus,
    &:active,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      background: #373b40 !important;
      color: #fff !important;
      caret-color: #fff !important;
    }
  }

  :-webkit-autofill,
  :-webkit-autofill:focus,
  :-webkit-autofill:hover {
    -webkit-box-shadow: 0 0 0 1000px #373b40 inset !important;
    box-shadow: 0 0 0 1000px #373b40 inset !important;
    -webkit-text-fill-color: #fff !important;
  }
`;

const Message = ({
  message,
  children,
}: {
  message: LandAdvisorMessage;
  children?: ReactNode;
}) => {
  const { id, role, text } = message;
  return (
    <div
      id={id}
      className={`d-flex justify-content-${role === 'user' ? 'end' : 'start'}`}
    >
      <MessageBubble $role={role}>
        {!!text?.length && (
          <Markdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            components={{
              a: NewTabAnchor, // Open links in a new tab
            }}
          >
            {text}
          </Markdown>
        )}
        {children}
      </MessageBubble>
    </div>
  );
};

const LoadingMessage = () => (
  <Message message={{ id: 'loading', role: 'agent', text: '' }}>
    <LoadingDots />
  </Message>
);

const LandAdvisorLeftPane = () => {
  const { parcels } = useLandAdvisorContext();
  const { loading, messages, sendMessage } = useLandAdvisorAgent({ parcels });

  // Ref for the scrollable messages container
  const messagesRef = useRef<HTMLDivElement | null>(null);
  // Ref for the textarea input
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const hasMessages = messages && messages.length > 0;

  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const handleSend = () => {
    const el = inputRef.current;
    if (!el) return;
    const value = (el.value || '').trim();
    if (!value) return;
    // clear the input and send
    el.value = '';
    sendMessage(value);
    // Scroll so the user's message is visible right away
    // Use requestAnimationFrame to allow the DOM to update
    requestAnimationFrame(() => scrollToBottom());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // Prevent the browser from inserting a newline
    e.preventDefault();
    handleSend();
  };

  const inputElement = (
    <InputRow>
      <SearchInput
        innerRef={inputRef}
        onKeyDown={onKeyDown}
        placeholder="Ask about your parcels"
        type="textarea"
        rows={1}
      />
      {messages.length && <SendButton onClick={handleSend} />}
    </InputRow>
  );

  // Auto-scroll when messages change or loading toggles
  useEffect(() => {
    // Small timeout to ensure DOM updates
    const t = window.setTimeout(() => scrollToBottom(), 0);
    return () => clearTimeout(t);
  }, [messages.length, loading]);

  return (
    <PaneContainer>
      <Header>
        <OveryieldLogoNameIcon height={30} width={150} />
      </Header>
      <ContentArea>
        {hasMessages ? (
          <>
            <MessagesScroll
              ref={messagesRef}
              aria-live="polite"
              aria-label="Conversation messages"
            >
              {messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
              {loading && <LoadingMessage />}
            </MessagesScroll>
            <InputFooter>{inputElement}</InputFooter>
          </>
        ) : (
          // No messages: center the input vertically
          <CenteredInputWrapper>{inputElement}</CenteredInputWrapper>
        )}
      </ContentArea>
    </PaneContainer>
  );
};

export default LandAdvisorLeftPane;
